/**
 * Number formatting utilities for consistent display and calculations
 */

/**
 * Format a number to a specific number of decimal places, removing trailing zeros
 * @param num - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 */
export function formatNumber(num: number, decimals: number = 2): string {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }

  // Round to avoid floating-point precision issues
  const rounded = Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
  
  // Format with specified decimal places
  const formatted = rounded.toFixed(decimals);
  
  // Remove trailing zeros after decimal point
  return formatted.replace(/\.?0+$/, '');
}

/**
 * Format a number for display with "credits" suffix
 * @param num - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with "credits" suffix
 */
export function formatCredits(num: number, decimals: number = 2): string {
  const formatted = formatNumber(num, decimals);
  return `${formatted} credit${formatted === '1' ? '' : 's'}`;
}

/**
 * Safely add two numbers, avoiding floating-point precision issues
 * @param a - First number
 * @param b - Second number
 * @returns Sum of the two numbers
 */
export function safeAdd(a: number, b: number): number {
  return Math.round((a + b) * 100) / 100;
}

/**
 * Safely subtract two numbers, avoiding floating-point precision issues
 * @param a - First number
 * @param b - Second number
 * @returns Difference of the two numbers
 */
export function safeSubtract(a: number, b: number): number {
  return Math.round((a - b) * 100) / 100;
}

/**
 * Safely multiply two numbers, avoiding floating-point precision issues
 * @param a - First number
 * @param b - Second number
 * @returns Product of the two numbers
 */
export function safeMultiply(a: number, b: number): number {
  return Math.round((a * b) * 100) / 100;
}

/**
 * Safely divide two numbers, avoiding floating-point precision issues
 * @param a - First number
 * @param b - Second number
 * @returns Quotient of the two numbers
 */
export function safeDivide(a: number, b: number): number {
  if (b === 0) return 0;
  return Math.round((a / b) * 100) / 100;
}

/**
 * Convert a number to a safe calculation value, handling null/undefined
 * @param num - The number to convert
 * @param defaultValue - Default value if num is null/undefined
 * @returns Safe number for calculations
 */
export function toSafeNumber(num: number | null | undefined, defaultValue: number = 0): number {
  if (num === null || num === undefined || isNaN(num)) {
    return defaultValue;
  }
  return num;
}

/**
 * Format a percentage with proper rounding
 * @param value - The percentage value (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  const rounded = Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
  const formatted = rounded.toFixed(decimals);
  // Remove trailing zeros after decimal point, but keep at least one decimal if decimals > 0
  const cleaned = decimals > 0 ? formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') : formatted;
  return `${cleaned}%`;
}

/**
 * Format a number for currency display
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'USD')
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Format a large number with commas
 * @param num - The number to format
 * @returns Formatted number string with commas
 */
export function formatLargeNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}